import { Box, IconButton, Menu, MenuItem, Button, Switch } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import SearchBar from "../../components/SearchBar";
import MainContainer from "../../layout/MainContainer";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import ResonOfRejection from "../../Modals/reasonOfRejection";

import { CommonBody, Listing } from "../../types/General";

import { VEHICLE_TYPE } from "../../utils/enums";
import { isValidInput } from "../../utils/validations";
import TableComponent from "../../components/TableComponent";
import Chip from "components/Chip";
import { Colors } from "constants/colors";
import {
  useLazyDeleteVehicleQuery,
  useLazyGetAllVehiclesQuery,
  useUpdateVechileMutation,
} from "service/vehicles";
import { showError, showToast } from "../../helpers/showToast";
import { generateEncryptedKeyBody } from "utils/crypto";
import CustomPagination from "components/Panigation";

const IndividualListings = () => {
  const label = { inputProps: { "aria-label": "Switch demo" } };
  const [getVehicles] = useLazyGetAllVehiclesQuery();
  const [deleteVehicle] = useLazyDeleteVehicleQuery();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [selectedId, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [rejectionOpen, setRejectionOpen] = useState<boolean>(false);
  const [rejectionRsn, setRejectionRsn] = useState<string>("");
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [udateVechile] = useUpdateVechileMutation();

  const handleStatusToggle = async (id: string, status: boolean) => {
    const body = {
      isActive: status,
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body);
      if (!encryptedBody) {
        showError("Encryption failed. Please try again.");
        return;
      }
      const response = await udateVechile({
        body: encryptedBody as CommonBody,
        id,
      }).unwrap();
      if (response?.statusCode === 200) {
        const updatedVehicles = vehicles.map((item) => {
          if (item?._id === id) {
            return {
              ...item,
              isActive: status,
            };
          }
          return item;
        });
        setVehicles(updatedVehicles);
        showToast(
          status ? "Activated successfully" : "Deactivated successfully"
        );
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handleFeatureToggle = async (id: string, isFeatured: boolean) => {
    const vehicle = vehicles.find((item) => item._id === id);
    const body = {
      flags: {
        isFeatured,
        isPopular: vehicle?.flags?.isPopular ?? false,
      },
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body);
      if (!encryptedBody) {
        showError("Encryption failed. Please try again.");
        return;
      }
      const response = await udateVechile({
        body: encryptedBody as CommonBody,
        id,
      }).unwrap();
      if (response?.statusCode === 200) {
        const updatedVehicles = vehicles.map((item) => {
          if (item?._id === id) {
            return {
              ...item,
              flags: {
                ...item.flags,
                isFeatured,
              },
            };
          }
          return item;
        });
        setVehicles(updatedVehicles);
        showToast(isFeatured ? "Marked as featured" : "Unmarked as featured");
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const columns: GridColDef[] = [
    {
      flex: 0.6,
      minWidth: 300,
      field: "vehicle",
      headerName: "Vehicle Details",
      editable: false,
      sortable: false,

      renderCell: ({ row }: any) => {
        return (
          <div className="sq_image" style={{ marginTop: "16px" }}>
            <figure style={{ cursor: "pointer" }} onClick={() => {}}>
              <img
                src={row?.images[0] || "/static/images/picture.png"}
                alt="Vehicle"
              />
            </figure>
            <p>
              <b>{row?.title?.en || "-"}</b>
            </p>
          </div>
        );
      },
    },
    {
      flex: 0.4,
      minWidth: 200,
      field: "vehicleType",
      headerName: "Vehicle Category",
      editable: false,
      sortable: false,
      renderCell: ({ row }: any) => {
        return (
          <div className="user_block">
            <p>
              <b>{row?.categoryDetails?.name?.en}</b>
            </p>
          </div>
        );
      },
    },
    {
      flex: 0.4,
      minWidth: 200,
      field: "brand",
      headerName: "Vehicle Brand",
      editable: false,
      sortable: false,
      renderCell: ({ row }: any) => {
        return (
          <div className="user_block">
            <p>
              <b>{row?.brandDetail?.name?.en}</b>
            </p>
          </div>
        );
      },
    },
    {
      flex: 0.4,
      minWidth: 100,
      field: "name",
      headerName: "Model",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.specs?.modelYear}</p>
          </div>
        );
      },
    },

    {
      flex: 0.4,
      minWidth: 150,
      field: "class",
      headerName: "Vehicle Class",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.classDetails?.name?.en || ""}</p>
          </div>
        );
      },
    },

    {
      flex: 0.4,
      minWidth: 150,
      field: "status",
      filterable: false,
      sortable: false,
      headerName: "Status",
      renderCell: ({ row }) => {
        return (
          <Switch
            {...label}
            size="small"
            checked={row?.isActive}
            onChange={() => handleStatusToggle(row?._id, !row?.isActive)}
          />
        );
      },
    },

    {
      flex: 0.4,
      minWidth: 150,
      field: "feature",
      filterable: false,
      sortable: false,
      headerName: "Feature Vehicle",
      renderCell: ({ row }) => {
        return (
          <Switch
            {...label}
            size="small"
            checked={row?.flags?.isFeatured}
            onChange={() =>
              handleFeatureToggle(row?._id, !row?.flags?.isFeatured)
            }
          />
        );
      },
    },

    {
      minWidth: 130,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="table_actions v2">
          <Box>
            <IconButton onClick={() => navigate(`/listing-detail/${row?._id}`)}>
              <img src="/static/images/eye_icon.svg" alt="" />
            </IconButton>
          </Box>
          <Box>
            <IconButton
              onClick={() => {
                navigate(`/listings/edit/${row?._id}`);
              }}
            >
              <img src="/static/images/edit2_icon.svg" alt="" />
            </IconButton>
          </Box>
          <Box>
            <IconButton
              onClick={() => {
                setSelectedId(row?._id);
                setOpen(true);
              }}
            >
              <img src="/static/images/trash2_icon.svg" alt="" />
            </IconButton>
          </Box>
        </div>
      ),
    },
  ];

  const getVehiclesApi = async () => {
    setLoading(true);
    try {
      const response = await getVehicles({
        page: page,
        search: debouncedSearchTerm,
        isSubscription: false,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setVehicles(response?.data?.vehicles);
        setCount(response?.data?.pagination?.total);
        setPage(response?.data?.pagination?.page);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const deleteVehicleApi = async (id: string) => {
    setLoading(true);
    try {
      const response = await deleteVehicle({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        getVehiclesApi();
        showToast(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setLoading(false);
    }
  };

  useEffect(() => {
    getVehiclesApi();
  }, [page, debouncedSearchTerm]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Manage Listed vehicles </h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Manage
          Listed vehicles
        </p>
      </div>

      <div className="stats_boxes gap_m" style={{ marginBottom: "30px" }}>
        <div
          className="cards"

          // onClick={() => navigate("/dashboard/registered-users")}
        >
          <figure>
            <img
              src="/static/images/car.png"
              alt=""
              style={{ height: "30px", width: "30px" }}
            />
          </figure>
          <h2>{vehicles?.length}</h2>
          <p>Total Cars</p>
        </div>

        <div
          className="cards"

          // onClick={() => navigate("/dashboard/registered-users")}
        >
          <figure>
            <img
              src="/static/images/checked.png"
              alt=""
              style={{ height: "30px", width: "30px" }}
            />
          </figure>
          <h2>17</h2>
          <p>Available</p>
        </div>

        <div
          className="cards"

          // onClick={() => navigate("/dashboard/registered-users")}
        >
          <figure>
            <img
              src="/static/images/warning.png"
              alt=""
              style={{ height: "30px", width: "30px" }}
            />
          </figure>
          <h2>7</h2>
          <p>Low Stock</p>
        </div>

        <div
          className="cards"

          // onClick={() => navigate("/dashboard/registered-users")}
        >
          <figure>
            <img
              src="/static/images/forbidden.png"
              alt=""
              style={{ height: "30px", width: "30px" }}
            />
          </figure>
          <h2>3</h2>
          <p>Out of Stock</p>
        </div>
      </div>

      <div className="cards">
        <Box className="custom_tabs" sx={{ width: "100%" }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            <div className="right_s">
              <Box>
                <Button
                  className="btn btn_orange"
                  onClick={() => {
                    navigate("/listings/add");
                  }}
                >
                  Add Vehicle
                </Button>
              </Box>
            </div>
          </div>

          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={vehicles}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={loading}
            />
            {vehicles?.length ? (
              <CustomPagination
                currentPage={page - 1}
                totalPages={Math.ceil(count / 10)}
                count={count}
                pageSize={10}
                onPageChange={(page) => setPage(page + 1)}
              />
            ) : null}
          </div>
        </Box>

        <ResonOfRejection
          selectedId={selectedId}
          rejectionRsn={rejectionRsn}
          setRejectionRsn={setRejectionRsn}
          open={rejectionOpen}
          setOpen={setRejectionOpen}
          onClose={() => {}}
        />

        <ConfirmationModal
          text={"Are you sure to delete this Vehicle"}
          openModal={open}
          setOpenModal={setOpen}
          handleSubmit={() => deleteVehicleApi(selectedId)}
        />
      </div>
    </MainContainer>
  );
};

export default IndividualListings;
